import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { ASSET_PATHS } from '@app/constants/asset-paths';

export interface SellerGuideStep {
  id: number;
  shortTitle: string;
  title: string;
  description: string;
  image: string;
  alt: string;
}

@Component({
  standalone: true,
  imports: [NgOptimizedImage],
  selector: 'app-sell-guide-card',
  templateUrl: './sell-guide-card.component.html',
  styleUrls: ['./sell-guide-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SellGuideCardComponent {
  readonly steps: SellerGuideStep[] = [
    {
      id: 1,
      shortTitle: 'Agent',
      title: 'Pick a Gabali Realty Agent',
      description:
        'The right agent sets your pricing strategy, marketing plan, and negotiation posture from day one. Our Laredo team studies local comps and buyer demand so you list with confidence.',
      image: ASSET_PATHS.guides.buyer.agents,
      alt: 'Choose a Gabali Realty agent',
    },
    {
      id: 2,
      shortTitle: 'Prep',
      title: 'Get Your Home Ready',
      description:
        'Small updates can shorten time on market. Your agent will walk through curb appeal, staging, and repairs that add value — and flag what is not worth the spend before you list.',
      image: ASSET_PATHS.guides.seller.getYourHomeReady,
      alt: 'Get your home ready to sell',
    },
    {
      id: 3,
      shortTitle: 'Pricing',
      title: 'Find Out How Much Your Home Is Worth',
      description:
        'Online estimates are a starting point, not a strategy. A Gabali agent compares recent sales, active competition, and your home’s condition to recommend a list price that attracts offers.',
      image: ASSET_PATHS.guides.seller.findHomeWorth,
      alt: 'Find out how much your home is worth',
    },
    {
      id: 4,
      shortTitle: 'Offer',
      title: 'Accept the Offer',
      description:
        'Once showings begin, offers may come in above, at, or below ask. Your agent explains each term — price, closing date, contingencies — and helps you counter, accept, or walk away with a clear plan.',
      image: ASSET_PATHS.guides.seller.acceptTheOffer,
      alt: 'Accept the offer',
    },
    {
      id: 5,
      shortTitle: 'Close',
      title: 'Close on Your Home',
      description:
        'Under contract, your agent tracks escrow deadlines, inspections, and title work through closing day. Most Texas sales close within 30–45 days of an accepted offer.',
      image: ASSET_PATHS.guides.seller.closeOnYourHome,
      alt: 'Close on your home',
    },
  ];

  readonly activeStep = signal(1);

  selectStep(stepId: number): void {
    this.activeStep.set(stepId);
  }
}
